import React, { useState, useEffect, useRef, useCallback } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import Alert from "@material-ui/lab/Alert";

import {
  Chat,
  People,
  Timer,
  Done as DoneIcon,
  Info as InfoIcon,
  FormatListBulletedSharp,
  HourglassEmpty,
  Forum,
  Reply,
  AssignmentLate,
  Star,
  CallReceived,
  CallMade,
  Speed,
  Refresh,
  HeadsetMic,
  WhatsApp,
} from "@material-ui/icons";

import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { socketConnection } from "../../services/socket";
import { taskDaysUntil, isTaskOverdue, taskDueSortKey, formatTaskDue } from "../../helpers/taskDue";

import Chart from "./Chart";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import CardCounter from "../../components/Dashboard/CardCounter";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import ConnectionsStatus from "../../components/Dashboard/ConnectionsStatus";
import BreakdownList from "../../components/Dashboard/BreakdownList";
import RatingSummary from "../../components/Dashboard/RatingSummary";
import Heatmap from "../../components/Dashboard/Heatmap";
import {
  formatMinutes,
  formatNumber,
  formatPercent,
  computeDelta,
} from "./format";

import useDashboard from "../../hooks/useDashboard";
import useCompanies from "../../hooks/useCompanies";
import useOnboardingStatus from "../../hooks/useOnboardingStatus";

import OnboardingChecklist from "../../components/OnboardingChecklist";
import WelcomeModal from "../../components/WelcomeModal";
import GuidedTour from "../../components/GuidedTour";

import { isArray } from "lodash";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    whiteSpace: "nowrap",
  },
  subscriptionFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.disabled,
    paddingTop: theme.spacing(1),
  },
  taskWidget: {
    padding: theme.spacing(2),
    height: "100%",
  },
  taskItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  taskDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  taskText: {
    flex: 1,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  taskDate: {
    fontSize: "0.7rem",
    flexShrink: 0,
  },
  taskWidgetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
  },
  updatedAt: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  chartPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: 300,
  },
  taskEmptyState: {
    textAlign: "center",
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
}));

const QUICK_FILTERS = [
  { key: "today",  label: "Hoje" },
  { key: "7",     label: "7 dias" },
  { key: "30",    label: "30 dias" },
  { key: "month", label: "Este mês" },
  { key: "custom", label: "Personalizado" },
];

const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#1e88e5" };

// Atualização automática dos números (só com a aba visível).
const REFRESH_INTERVAL_MS = 60 * 1000;

const EMPTY_DATA = {
  counters: {},
  previous: {},
  attendants: [],
  connections: [],
  byQueue: [],
  byConnection: [],
  daily: [],
  heatmap: [],
  ratingDistribution: [],
  period: { days: 1 },
  slaMinutes: 15,
};

const Dashboard = () => {
  const classes = useStyles();
  const history = useHistory();

  const [data, setData]                 = useState(EMPTY_DATA);
  const [loading, setLoading]           = useState(false);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [companyDueDate, setCompanyDueDate] = useState(null);
  const [tasks, setTasks]               = useState([]);
  const [runTour, setRunTour]           = useState(false);

  const [quickFilter, setQuickFilter]   = useState("today");
  const [dateFrom, setDateFrom]         = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo]             = useState(moment().format("YYYY-MM-DD"));

  // Período efetivamente aplicado (usado pela atualização automática).
  const appliedPeriod = useRef({
    from: moment().format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
    quick: "today",
  });
  // Descarta respostas antigas quando o filtro muda rápido.
  const requestId = useRef(0);

  const { find }    = useDashboard();
  const { finding } = useCompanies();
  const onboarding  = useOnboardingStatus();
  const companyId   = localStorage.getItem("companyId");

  // null enquanto carrega; true = primeiro acesso; false = conta já configurada
  const isNewAccount = onboarding.loading ? null : !onboarding.isEstablished;

  const { counters, previous } = data;

  const fetchData = useCallback(async (from, to, { silent = false } = {}) => {
    const id = ++requestId.current;
    if (!silent) setLoading(true);
    try {
      const result = await find({ date_from: from, date_to: to });
      if (id !== requestId.current) return;
      setData({
        ...EMPTY_DATA,
        ...result,
        counters: result.counters || {},
        previous: result.previous || {},
        attendants: isArray(result.attendants) ? result.attendants : [],
      });
      setLastUpdate(moment());
    } catch (e) {
      if (id === requestId.current && !silent) {
        toast.error("Erro ao carregar dados do dashboard.");
      }
    }
    if (id === requestId.current && !silent) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCompany();
    fetchData(appliedPeriod.current.from, appliedPeriod.current.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Status dos agentes em tempo real: o backend avisa quando alguém entra ou
  // sai, sem esperar o refetch de 1 minuto.
  useEffect(() => {
    if (!companyId) return;
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-userPresence`, ({ userId, online, away }) => {
      setData((prev) => {
        if (!prev.attendants.some((a) => Number(a.id) === Number(userId))) return prev;
        return {
          ...prev,
          attendants: prev.attendants.map((a) =>
            Number(a.id) === Number(userId) ? { ...a, online, away: Boolean(online && away) } : a
          ),
        };
      });
    });
    return () => socket.disconnect();
  }, [companyId]);

  useEffect(() => {
    api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden) return;
      const period = appliedPeriod.current;
      // Filtros relativos ("Hoje", "7 dias"...) acompanham a virada do dia.
      if (period.quick !== "custom") {
        const range = rangeFor(period.quick);
        period.from = range.from;
        period.to = range.to;
      }
      fetchData(period.from, period.to, { silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchData]);

  const loadCompany = async () => {
    try {
      const company = await finding(companyId);
      if (company?.dueDate) setCompanyDueDate(moment(company.dueDate));
    } catch (e) {}
  };

  const rangeFor = (key) => {
    const today = moment().format("YYYY-MM-DD");
    let from = today;
    if (key === "7")     from = moment().subtract(6, "days").format("YYYY-MM-DD");
    if (key === "30")    from = moment().subtract(29, "days").format("YYYY-MM-DD");
    if (key === "month") from = moment().startOf("month").format("YYYY-MM-DD");
    return { from, to: today };
  };

  const applyQuickFilter = (key) => {
    setQuickFilter(key);
    if (key === "custom") return;

    const { from, to } = rangeFor(key);
    setDateFrom(from);
    setDateTo(to);
    appliedPeriod.current = { from, to, quick: key };
    fetchData(from, to);
  };

  const handleCustomFilter = () => {
    if (!moment(dateFrom, "YYYY-MM-DD", true).isValid() || !moment(dateTo, "YYYY-MM-DD", true).isValid()) {
      toast.error("Preencha as datas corretamente.");
      return;
    }
    if (dateFrom > dateTo) {
      toast.error("A data inicial deve ser anterior à data final.");
      return;
    }
    if (moment(dateTo).diff(moment(dateFrom), "days") > 365) {
      toast.error("Escolha um período de até 1 ano.");
      return;
    }
    appliedPeriod.current = { from: dateFrom, to: dateTo, quick: "custom" };
    fetchData(dateFrom, dateTo);
  };

  const handleRefresh = () => {
    const { from, to } = appliedPeriod.current;
    fetchData(from, to);
  };

  // Vencimento por dia de calendário (vence hoje ≠ vencida).
  const daysUntilExpiry  = companyDueDate
    ? companyDueDate.clone().startOf("day").diff(moment().startOf("day"), "days")
    : null;
  const showExpiryAlert  = daysUntilExpiry !== null && daysUntilExpiry <= 7;
  const showExpiryFooter = !showExpiryAlert && companyDueDate !== null;

  const expiryMessage = () => {
    const date = companyDueDate.format("DD/MM/YYYY");
    if (daysUntilExpiry < 0) return `Assinatura vencida em ${date}. Entre em contato para renovar.`;
    if (daysUntilExpiry === 0) return `Assinatura vence hoje (${date}).`;
    return `Assinatura vence em ${daysUntilExpiry} dia(s) — ${date}.`;
  };

  const onlineCount = data.attendants.filter((a) => a.online).length;
  const disconnectedCount = data.connections.filter((c) => c.status !== "CONNECTED").length;

  const days = data.period?.days || 1;
  const comparisonLabel = days === 1 ? "vs. dia anterior" : `vs. ${days} dias anteriores`;
  const delta = (key, options) => computeDelta(counters[key], previous[key], options);

  // prazo lido pelo helper das tarefas (dia todo x horário, formatos antigos)
  const upcomingTasks = tasks
    .filter((t) => {
      if (!t.dueDate || t.completed) return false;
      const days = taskDaysUntil(t);
      return days !== null && days < 7;
    })
    .sort((a, b) => {
      const aOverdue = isTaskOverdue(a);
      const bOverdue = isTaskOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      return taskDueSortKey(a) - taskDueSortKey(b);
    })
    .slice(0, 6);
  const pendingCount = tasks.filter((t) => !t.completed).length;

  const periodCards = [
    {
      title: "Conversas",
      icon: <Forum fontSize="inherit" />,
      color: "#1565c0",
      value: formatNumber(counters.supportStarted),
      delta: delta("supportStarted"),
      tooltip: "Conversas abertas no período (grupos não entram).",
    },
    {
      title: "Finalizados",
      icon: <DoneIcon fontSize="inherit" />,
      color: "#2e7d32",
      value: formatNumber(counters.supportFinished),
      delta: delta("supportFinished"),
      tooltip: "Atendimentos encerrados no período.",
    },
    {
      title: "Novos contatos",
      icon: <People fontSize="inherit" />,
      color: "#6a1b9a",
      value: formatNumber(counters.newContacts),
      delta: delta("newContacts"),
      hint:
        counters.contactsServed
          ? `${formatNumber(counters.returningContacts)} recorrente${counters.returningContacts === 1 ? "" : "s"}`
          : null,
      tooltip: "Contatos cadastrados no período. Recorrentes: contatos antigos que conversaram no período.",
    },
    {
      title: "Sem atendimento",
      icon: <AssignmentLate fontSize="inherit" />,
      color: "#c62828",
      value: formatNumber(counters.abandoned),
      delta: delta("abandoned", { inverse: true }),
      tooltip: "Entraram na fila de espera e foram encerrados sem nenhum agente assumir.",
    },
    {
      title: "T.M. de Espera",
      icon: <Timer fontSize="inherit" />,
      color: "#bf360c",
      value: formatMinutes(counters.avgWaitTime),
      delta: delta("avgWaitTime", { inverse: true }),
      tooltip: "Tempo médio entre entrar na fila de espera e um atendente assumir.",
    },
    {
      title: "1ª resposta",
      icon: <Reply fontSize="inherit" />,
      color: "#00838f",
      value: formatMinutes(counters.avgFirstResponseTime),
      delta: delta("avgFirstResponseTime", { inverse: true }),
      tooltip: "Tempo médio entre a primeira mensagem do cliente e a primeira resposta do agente.",
    },
    {
      title: "T.M. de Atendimento",
      icon: <Timer fontSize="inherit" />,
      color: "#00695c",
      value: formatMinutes(counters.avgSupportTime),
      delta: delta("avgSupportTime", { inverse: true }),
      tooltip: "Tempo médio entre o agente assumir e encerrar o atendimento.",
    },
    {
      title: `Espera ≤ ${data.slaMinutes || 15} min`,
      icon: <Speed fontSize="inherit" />,
      color: "#283593",
      value: formatPercent(counters.slaRate),
      delta: delta("slaRate"),
      tooltip: `Percentual dos atendimentos assumidos em até ${data.slaMinutes || 15} minutos de espera.`,
    },
    {
      title: "Avaliação média",
      icon: <Star fontSize="inherit" />,
      color: "#f9a825",
      value:
        counters.ratingAvg !== null && counters.ratingAvg !== undefined
          ? `${counters.ratingAvg.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} / 5`
          : "—",
      delta: delta("ratingAvg"),
      hint: counters.ratingCount ? `${formatNumber(counters.ratingCount)} avaliações` : null,
      tooltip: "Média das notas de 1 a 5 enviadas pelos clientes. Avaliações antigas (de 1 a 3) são convertidas para a nova escala.",
    },
    {
      title: "Msgs recebidas",
      icon: <CallReceived fontSize="inherit" />,
      color: "#455a64",
      value: formatNumber(counters.messagesReceived),
      delta: delta("messagesReceived"),
      tooltip: "Mensagens recebidas dos clientes no período (sem grupos).",
    },
    {
      title: "Msgs enviadas",
      icon: <CallMade fontSize="inherit" />,
      color: "#455a64",
      value: formatNumber(counters.messagesSent),
      delta: delta("messagesSent"),
      tooltip: "Mensagens enviadas no período, incluindo automáticas (notas internas não entram).",
    },
  ];

  return (
    <div>
      <WelcomeModal
        isNewAccount={isNewAccount}
        onGuide={() => {
          // Pequeno delay para o modal fechar antes do tour iniciar
          setTimeout(() => setRunTour(true), 400);
        }}
      />
      <GuidedTour run={runTour} onFinish={() => setRunTour(false)} />

      <Container maxWidth="lg" className={classes.container}>
        <Grid container spacing={3}>

          {/* Checklist de primeiros passos */}
          <Grid item xs={12} data-tour="onboarding-checklist">
            <OnboardingChecklist counters={counters} status={onboarding} />
          </Grid>

          {/* Alerta de vencimento — só quando ≤ 7 dias */}
          {showExpiryAlert && (
            <Grid item xs={12}>
              <Alert severity={daysUntilExpiry < 0 ? "error" : "warning"}>
                {expiryMessage()}
              </Alert>
            </Grid>
          )}

          {/* Status das conexões WhatsApp — só quando alguma precisa de atenção */}
          {disconnectedCount > 0 && (
            <Grid item xs={12}>
              <ConnectionsStatus connections={data.connections} />
            </Grid>
          )}

          {/* Agora */}
          <Grid item xs={12} style={{ paddingBottom: 0 }}>
            <Typography className={classes.sectionLabel}>Agora</Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<HourglassEmpty fontSize="inherit" />}
              title="Pendentes"
              value={formatNumber(counters.supportPending)}
              loading={loading}
              color="#f57c00"
              tooltip="Tickets aguardando atendimento neste momento."
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<Chat fontSize="inherit" />}
              title="Em atendimento"
              value={formatNumber(counters.supportHappening)}
              loading={loading}
              color="#1565c0"
              tooltip="Tickets abertos com um agente neste momento."
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<HeadsetMic fontSize="inherit" />}
              title="Agentes online"
              value={
                data.attendants.length
                  ? `${onlineCount} / ${data.attendants.length}`
                  : "—"
              }
              loading={loading}
              color="#2e7d32"
              tooltip="Agentes conectados ao sistema neste momento."
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<WhatsApp fontSize="inherit" />}
              title="Conexões ativas"
              value={
                data.connections.length
                  ? `${data.connections.length - disconnectedCount} / ${data.connections.length}`
                  : "—"
              }
              loading={loading}
              color={disconnectedCount > 0 ? "#c62828" : "#2e7d32"}
              hint={disconnectedCount > 0 ? "Verifique as conexões" : null}
              tooltip="Conexões WhatsApp conectadas neste momento."
            />
          </Grid>

          {/* Filtros rápidos */}
          <Grid item xs={12} style={{ paddingBottom: 0 }}>
            <div className={classes.filterRow}>
              <Typography variant="body2" className={classes.filterLabel}>
                Período:
              </Typography>
              {QUICK_FILTERS.map(({ key, label }) => (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  clickable
                  onClick={() => applyQuickFilter(key)}
                  color={quickFilter === key ? "primary" : "default"}
                  variant={quickFilter === key ? "default" : "outlined"}
                />
              ))}
              <div className={classes.updatedAt}>
                {lastUpdate && (
                  <Typography variant="caption">
                    Atualizado às {lastUpdate.format("HH:mm")}
                  </Typography>
                )}
                <Tooltip title="Atualizar agora">
                  <span>
                    <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                      <Refresh fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            {/* Campos de data — só no modo personalizado */}
            {quickFilter === "custom" && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 8 }}>
                <TextField
                  label="Data Inicial"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Data Final"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                />
                <ButtonWithSpinner
                  loading={loading}
                  onClick={handleCustomFilter}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  Filtrar
                </ButtonWithSpinner>
              </Box>
            )}
            <Typography variant="caption" color="textSecondary">
              Comparação {comparisonLabel}
            </Typography>
          </Grid>

          {/* KPIs do período */}
          {periodCards.map((card) => (
            <Grid item xs={6} sm={6} md={3} key={card.title}>
              <CardCounter {...card} loading={loading} />
            </Grid>
          ))}

          {/* Gráfico */}
          <Grid item xs={12}>
            <Paper className={classes.chartPaper}>
              <Chart
                daily={data.daily}
                heatmap={data.heatmap}
                days={days}
                loading={loading}
              />
            </Paper>
          </Grid>

          {/* Distribuições */}
          <Grid item xs={12} md={4}>
            <BreakdownList
              title="Por departamento"
              subtitle="Conversas com mensagens no período"
              items={data.byQueue}
              loading={loading}
              emptyText="Nenhuma conversa com departamento no período"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <BreakdownList
              title="Por conexão"
              subtitle="Conversas abertas no período"
              items={data.byConnection}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RatingSummary
              counters={counters}
              distribution={data.ratingDistribution}
              loading={loading}
            />
          </Grid>

          {/* Horários de pico — útil a partir de uma semana de dados */}
          {days >= 7 && (
            <Grid item xs={12} md={8}>
              <Heatmap heatmap={data.heatmap} />
            </Grid>
          )}

          {/* Tarefas da Semana */}
          <Grid item xs={12} md={days >= 7 ? 4 : 12}>
            <Paper className={classes.taskWidget}>
              <div className={classes.taskWidgetHeader}>
                <div>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <FormatListBulletedSharp style={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      Tarefas
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>

              {upcomingTasks.length === 0 ? (
                <div className={classes.taskEmptyState}>
                  Nenhuma tarefa próxima
                </div>
              ) : (
                upcomingTasks.map((task) => {
                  const isOverdue = isTaskOverdue(task);
                  const dateLabel = formatTaskDue(task, { year: false });
                  const text = task.text || "";
                  const truncated = text.length > 35
                    ? text.substring(0, 35) + "…"
                    : text;
                  return (
                    <div key={task.id} className={classes.taskItem}>
                      <span
                        className={classes.taskDot}
                        style={{ backgroundColor: PRIORITY_COLORS[task.priority] || "#9e9e9e" }}
                      />
                      <span className={classes.taskText} title={text}>
                        {truncated}
                      </span>
                      <Chip
                        label={dateLabel}
                        size="small"
                        className={classes.taskDate}
                        style={isOverdue
                          ? { backgroundColor: "#e53935", color: "#fff", fontWeight: 700, height: 20 }
                          : { height: 20 }
                        }
                      />
                    </div>
                  );
                })
              )}

              <Box mt={1.5}>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => history.push("/todolist")}
                >
                  Ver todas
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Tabela de agentes */}
          {data.attendants.length > 0 && (
            <Grid item xs={12}>
              <TableAttendantsStatus attendants={data.attendants} loading={loading} />
            </Grid>
          )}

          {/* Rodapé sutil de assinatura */}
          {showExpiryFooter && (
            <Grid item xs={12}>
              <div className={classes.subscriptionFooter}>
                <InfoIcon style={{ fontSize: 14 }} />
                <Typography variant="caption">
                  Assinatura ativa até {companyDueDate.format("DD/MM/YYYY")}
                </Typography>
              </div>
            </Grid>
          )}

        </Grid>
      </Container>
    </div>
  );
};

export default Dashboard;
